import type { ReactNode } from 'react';
import React from 'react';
import { Button, KIND, SIZE } from 'baseui/button';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { insertIf } from '../utils/utils';
export const TOOLBAR_BUTTON_SIZE = Object.freeze({
  REGULAR: 'REGULAR',
  SMALL: 'SMALL'
});
type ToolbarButtonPropsT = {
  title: string;
  onClick?: () => void;
  onMouseOut?: () => void;
  onMouseOver?: () => void;
  children: ReactNode;
  disabled?: boolean;
  isSelected?: boolean;
  size?: (typeof TOOLBAR_BUTTON_SIZE)[keyof typeof TOOLBAR_BUTTON_SIZE];
  attributes?: {
    [name: string]: string;
  };
  customStyles?: React.CSSProperties;
};

// Note: We are visually disabling the button using disabled styles as
// StatefulTooltip break, as a disabled button doesn't fire mouse events
// Baseweb team knows this, but have not fixed it yet
const getDisabledStyles = $theme => ({
  cursor: 'not-allowed',
  backgroundColor: 'transparent',
  color: $theme.colors.contentStateDisabled,
  ':hover': {
    backgroundColor: 'transparent'
  }
});
function ToolbarButton({
  children,
  title,
  onClick,
  disabled,
  isSelected,
  size = TOOLBAR_BUTTON_SIZE.REGULAR,
  attributes,
  customStyles = {}
}: ToolbarButtonPropsT) {
  return <StatefulTooltip content={title} placement={PLACEMENT.top} showArrow>
      <Button onClick={e => {
      if (disabled) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      onClick?.();
    }} overrides={{
      BaseButton: {
        style: ({
          $theme
        }) => ({
          marginLeft: customStyles.marginLeft ?? $theme.sizing.scale100,
          marginBottom: customStyles?.marginBottom,
          outline: 'none',
          boxShadow: 'none',
          position: 'relative',
          height: size === TOOLBAR_BUTTON_SIZE.SMALL ? '32px' : '44px',
          width: size === TOOLBAR_BUTTON_SIZE.SMALL ? '32px' : '44px',
          backgroundColor: isSelected ? $theme.colors.backgroundTertiary : $theme.colors.backgroundPrimary,
          ...(disabled ? getDisabledStyles($theme) : {}),
          ...(size === TOOLBAR_BUTTON_SIZE.SMALL ? {
            padding: customStyles.padding ?? '8px',
            marginBottom: '4px'
          } : {}),
          ...insertIf({
            borderRadius: customStyles.borderRadius
          }, !!customStyles.borderRadius)
        })
      }
    }} kind={KIND.tertiary} size={SIZE.compact} {...attributes} aria-disabled={disabled}>
        {children}
      </Button>
    </StatefulTooltip>;
}
export default ToolbarButton;